// src/modules/app-views/account/pages/Subscription/modals/CertificateUploadModal/certificateParser.js
// *******************************************************************************
// CERTIFICATE PARSER - Parses PFX/P12 certificates using node-forge
// *******************************************************************************
import forge from 'node-forge';

/**
 * Parses a PFX/P12 certificate and extracts its information
 *
 * @param {string} base64Data - Base64 encoded certificate file
 * @param {string} password - Certificate password
 * @returns {Promise<Object>} Parsed certificate info
 *
 * @example
 * const result = await parseCertificate(base64Data, 'password123');
 * if (result.success) {
 *   console.log(result.data.holderName);
 *   console.log(result.data.expiresAt);
 * }
 */
export async function parseCertificate(base64Data, password) {
  try {
    // Decode base64 to binary
    const binaryData = forge.util.decode64(base64Data);

    // Parse PKCS#12 (PFX)
    const p12Asn1 = forge.asn1.fromDer(binaryData);
    const p12 = forge.pkcs12.pkcs12FromAsn1(p12Asn1, password);

    // Extract certificate bags
    const certBags = p12.getBags({ bagType: forge.pki.oids.certBag });
    const certBagArray = certBags[forge.pki.oids.certBag];

    if (!certBagArray || certBagArray.length === 0) {
      return {
        success: false,
        error: 'no_certificate_found_in_file',
      };
    }

    // Get the first certificate (usually the main one)
    const cert = certBagArray[0].cert;

    if (!cert) {
      return {
        success: false,
        error: 'invalid_certificate_structure',
      };
    }

    // Extract subject (holder) information
    const subject = cert.subject.attributes;
    const issuer = cert.issuer.attributes;

    // Helper to get attribute value by shortName
    const getAttr = (attrs, shortName) => {
      const attr = attrs.find(a => a.shortName === shortName);
      return attr?.value || null;
    };

    // Extract holder name (CN = Common Name)
    // Brazilian certificates format: "COMPANY NAME:CNPJ" or "PERSON NAME:CPF"
    const rawCN = getAttr(subject, 'CN') || getAttr(subject, 'O') || 'Unknown';

    // Parse CN to extract name and document
    let holderName = rawCN;
    let cnpj = null;
    let cpf = null;

    // Check if CN contains document after colon (Brazilian format)
    if (rawCN.includes(':')) {
      const parts = rawCN.split(':');
      const lastPart = parts[parts.length - 1].trim();

      // Check if last part is a CNPJ (14 digits) or CPF (11 digits)
      const cleanDoc = lastPart.replace(/\D/g, '');

      if (cleanDoc.length === 14) {
        // CNPJ found in CN
        cnpj = cleanDoc;
        holderName = parts.slice(0, -1).join(':').trim();
      } else if (cleanDoc.length === 11) {
        // CPF found in CN
        cpf = cleanDoc;
        holderName = parts.slice(0, -1).join(':').trim();
      }
    }

    // Fallback: try to extract from serialNumber or OU if not found in CN
    if (!cnpj && !cpf) {
      const serialNumberAttr = getAttr(subject, 'serialNumber') || '';
      const ouAttr = getAttr(subject, 'OU') || '';
      const searchStr = serialNumberAttr + ouAttr;

      // CNPJ pattern: 14 digits
      const cnpjMatch = searchStr.match(/\d{14}/);
      cnpj = cnpjMatch ? cnpjMatch[0] : null;

      // CPF pattern: 11 digits (if CNPJ not found)
      if (!cnpj) {
        const cpfMatch = searchStr.match(/\d{11}/);
        cpf = cpfMatch ? cpfMatch[0] : null;
      }
    }

    // Extract issuer information
    const issuerCN = getAttr(issuer, 'CN') || 'Unknown Issuer';
    const issuerO = getAttr(issuer, 'O') || '';
    const issuerName = issuerO ? `${issuerCN} - ${issuerO}` : issuerCN;

    // Extract dates
    const validFrom = cert.validity.notBefore;
    const expiresAt = cert.validity.notAfter;

    // Calculate days until expiry
    const now = new Date();
    const expiryDate = new Date(expiresAt);
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

    // Extract serial number
    const serialNumber = cert.serialNumber;

    // Check if expired
    const isExpired = daysUntilExpiry < 0;
    const isExpiringSoon = daysUntilExpiry >= 0 && daysUntilExpiry <= 30;

    // Extract key usage (for validation)
    const extensions = cert.extensions || [];
    const keyUsageExt = extensions.find(e => e.name === 'keyUsage');
    const keyUsage = keyUsageExt ? Object.keys(keyUsageExt).filter(k => keyUsageExt[k] === true) : [];

    return {
      success: true,
      data: {
        // Holder information
        holderName,
        cnpj,
        cpf,
        document: cnpj || cpf,
        documentType: cnpj ? 'cnpj' : cpf ? 'cpf' : null,

        // Certificate details
        serialNumber,
        issuer: issuerName,

        // Validity
        validFrom: validFrom.toISOString(),
        expiresAt: expiresAt.toISOString(),
        daysUntilExpiry,
        isExpired,
        isExpiringSoon,

        // Status
        status: isExpired ? 'expired' : isExpiringSoon ? 'expiring' : 'valid',

        // Technical details
        keyUsage,
        version: cert.version,

        // Original file info (for reference)
        fileSize: base64Data.length,
      },
    };
  } catch (error) {
    console.error('[certificateParser] Error:', error);

    // Check for common errors
    if (error.message?.includes('Invalid password') || error.message?.includes('PKCS#12')) {
      return {
        success: false,
        error: 'invalid_certificate_password',
      };
    }

    if (error.message?.includes('ASN.1') || error.message?.includes('parse')) {
      return {
        success: false,
        error: 'invalid_certificate_format',
      };
    }

    return {
      success: false,
      error: 'certificate_parse_error',
      details: error.message,
    };
  }
}

/**
 * Validates if a certificate is suitable for NFe/NFCe emission
 *
 * @param {Object} certInfo - Parsed certificate info from parseCertificate
 * @returns {Object} Validation result
 */
export function validateCertificateForNFe(certInfo) {
  const issues = [];

  if (!certInfo || !certInfo.success) {
    return { valid: false, issues: ['invalid_certificate'] };
  }

  const { data } = certInfo;

  // Check expiration
  if (data.isExpired) {
    issues.push('certificate_expired');
  } else if (data.isExpiringSoon) {
    issues.push('certificate_expiring_soon');
  }

  // Check document (CNPJ is required for company certificates)
  if (!data.cnpj) {
    issues.push('certificate_missing_cnpj');
  }

  return {
    valid: issues.length === 0,
    issues,
    warnings: data.isExpiringSoon ? ['certificate_expiring_soon'] : [],
  };
}

export default {
  parseCertificate,
  validateCertificateForNFe,
};
